import { useState, useRef, useEffect } from "react";
import { createRoot } from "react-dom/client";

import { AudioContext } from "./hooks/useAudio";
import { STREAMS, NOWPLAYING_DELAY_SECONDS, type StreamId } from "./constants";
import { HomeView, StreamView, PlayerBar, UnlockOverlay } from "./components";
import type { AudioState, NowPlaying } from "./types";

// Parse initial route from URL
function getInitialView(): "home" | StreamId {
  const path = window.location.pathname.slice(1);
  if (path && path in STREAMS) {
    return path as StreamId;
  }
  return "home";
}

function App() {
  const [view, setView] = useState<"home" | StreamId>(getInitialView);
  const [audioState, setAudioState] = useState<AudioState>({
    currentStream: "music",
    isPlaying: false,
    volume: 0.8,
    isMuted: false,
  });
  const [needsUnlock, setNeedsUnlock] = useState(true);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [pendingNowPlaying, setPendingNowPlaying] = useState<NowPlaying | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Navigate and update URL
  const navigate = (newView: "home" | StreamId) => {
    setView(newView);
    const url = newView === "home" ? "/" : `/${newView}`;
    window.history.pushState({ view: newView }, "", url);
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.view) {
        setView(event.state.view);
      } else {
        setView(getInitialView());
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.history.replaceState({ view }, "", window.location.pathname);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Poll for now playing info
  useEffect(() => {
    const fetchNowPlaying = () => {
      if (audioState.isPlaying) {
        fetch(`/api/nowplaying/${audioState.currentStream}`)
          .then((r) => r.json())
          .then((data) => {
            if (data.title) {
              setPendingNowPlaying(data);
            }
          })
          .catch(() => {});
      }
    };

    const handleRefresh = () => {
      setTimeout(fetchNowPlaying, 500);
    };
    window.addEventListener("nowplaying-refresh", handleRefresh);

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 5000);
    return () => {
      clearInterval(interval);
      window.removeEventListener("nowplaying-refresh", handleRefresh);
    };
  }, [audioState.currentStream, audioState.isPlaying]);

  // Delay nowplaying display to sync with audio latency
  useEffect(() => {
    if (!pendingNowPlaying) return;

    const displayAtTime = (pendingNowPlaying.timestamp + NOWPLAYING_DELAY_SECONDS) * 1000;
    const now = Date.now();
    const delay = displayAtTime - now;

    if (delay <= 0 || !pendingNowPlaying.timestamp) {
      setNowPlaying(pendingNowPlaying);
      return;
    }

    const timer = setTimeout(() => {
      setNowPlaying(pendingNowPlaying);
    }, delay);

    return () => clearTimeout(timer);
  }, [pendingNowPlaying]);

  // Try autoplay on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.volume = audioState.volume;
        const streamToPlay = view !== "home" ? view : audioState.currentStream;
        audioRef.current.src = `https://radio.swansong.city/${streamToPlay}.ogg`;
        audioRef.current.play().then(() => {
          setAudioState((prev) => ({ ...prev, currentStream: streamToPlay, isPlaying: true }));
          setNeedsUnlock(false);
        }).catch(() => {});
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const selectStream = (id: StreamId) => {
    const expectedSrc = `https://radio.swansong.city/${id}.ogg`;

    // Don't reload if already on this stream with correct src
    if (audioState.currentStream === id && audioRef.current?.src === expectedSrc) {
      return;
    }

    setAudioState((prev) => ({ ...prev, currentStream: id }));
    setNowPlaying(null);
    setPendingNowPlaying(null);
    if (audioRef.current) {
      audioRef.current.src = expectedSrc;
      audioRef.current.play().then(() => {
        setAudioState((prev) => ({ ...prev, isPlaying: true }));
      }).catch(() => {});
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (audioState.isPlaying) {
      audioRef.current.pause();
      setAudioState((prev) => ({ ...prev, isPlaying: false }));
    } else {
      if (!audioRef.current.src) {
        audioRef.current.src = `https://radio.swansong.city/${audioState.currentStream}.ogg`;
      }
      audioRef.current.play().then(() => {
        setAudioState((prev) => ({ ...prev, isPlaying: true }));
      }).catch(() => {});
    }
  };

  const setVolume = (v: number) => {
    setAudioState((prev) => ({ ...prev, volume: v, isMuted: v === 0 }));
    if (audioRef.current) {
      audioRef.current.volume = v;
    }
  };

  const toggleMute = () => {
    setAudioState((prev) => {
      const newMuted = !prev.isMuted;
      if (audioRef.current) {
        audioRef.current.volume = newMuted ? 0 : prev.volume;
      }
      return { ...prev, isMuted: newMuted };
    });
  };

  const handleUnlockAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setNeedsUnlock(false);

    if (audioRef.current) {
      const streamToPlay = view !== "home" ? view : audioState.currentStream;
      audioRef.current.src = `https://radio.swansong.city/${streamToPlay}.ogg`;
      audioRef.current.volume = audioState.volume;
      audioRef.current.load();
      audioRef.current.play().then(() => {
        setAudioState((prev) => ({ ...prev, currentStream: streamToPlay, isPlaying: true }));
      }).catch((err) => {
        console.error("Play failed:", err);
      });
    }
  };

  // Track whether user has ever unlocked audio (survives re-renders)
  const hasUnlockedRef = useRef(false);
  useEffect(() => {
    if (!needsUnlock) hasUnlockedRef.current = true;
  }, [needsUnlock]);

  // Audio element event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setAudioState((p) => ({ ...p, isPlaying: true }));
    const handlePause = () => {
      setAudioState((p) => ({ ...p, isPlaying: false }));
    };
    const handleError = () => {
      setAudioState((p) => ({ ...p, isPlaying: false }));
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <AudioContext.Provider
      value={{
        state: audioState,
        audioRef,
        selectStream,
        togglePlay,
        setVolume,
        toggleMute,
        nowPlaying,
      }}
    >
      <audio ref={audioRef} preload="none" />

      {needsUnlock && <UnlockOverlay onUnlock={handleUnlockAudio} />}

      <div className="gradient-mesh" />
      <div className="noise-overlay" />

      {view === "home" ? (
        <HomeView onNavigate={navigate} />
      ) : (
        <StreamView id={view} onBack={() => navigate("home")} />
      )}

      <PlayerBar />
    </AudioContext.Provider>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
